import { useMemo, type ComponentType } from 'react';
import { useTranslation } from 'react-i18next';
import { format, parseISO } from 'date-fns';
import styled from '@emotion/styled';
import SelectRaw from 'components/Inputs/Select';
import { Menu } from 'components/Inputs/Select/customize';
import { OutlineLabel } from 'components/Inputs/InputControl';
import type { BillingGetAIUsageCycles } from '@kizen/api/billing';
import { CYCLE_SELECT_MENU_Z_INDEX } from './styles';

const Select = SelectRaw as ComponentType<any>;

const CycleSelectRow = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
`;

const CycleSelectLabel = styled(OutlineLabel)`
  margin-bottom: 0;
`;

const CycleSelectWrapper = styled.div`
  width: 307px;
`;

const CycleSelectMenu = (props: any) => (
  <Menu {...props} zIndexAdjust={CYCLE_SELECT_MENU_Z_INDEX} />
);

type Option = { value: string; label: string };

type Props = {
  cycles?: BillingGetAIUsageCycles;
  value?: string;
  onChange: (usageCycle: string) => void;
};

// parseISO (not `new Date`) so a date-only string like "2026-08-01" parses
// in local time — `new Date("2026-08-01")` parses as UTC midnight, which
// then displays as the prior day in negative-UTC-offset timezones.
const formatCycleDate = (date: string) =>
  format(parseISO(date), 'MMM dd, yyyy');

export const UsageCycleSelect = ({ cycles, value, onChange }: Props) => {
  const { t } = useTranslation();

  const options = useMemo<Option[]>(() => {
    if (!cycles) {
      return [];
    }

    // BE returns cycles oldest-first; show latest (current) on top.
    return [...cycles.results].reverse().map((cycle) => {
      const isCurrent = cycle.usage_cycle === cycles.current_usage_cycle;
      return {
        value: cycle.usage_cycle,
        label: isCurrent
          ? t('Current (from {{start}})', {
              start: formatCycleDate(cycle.usage_cycle),
            })
          : `${formatCycleDate(cycle.usage_cycle)} – ${formatCycleDate(cycle.end_date)}`,
      };
    });
  }, [cycles, t]);

  const selectedValue = value ?? cycles?.current_usage_cycle;
  const selected =
    options.find((option) => option.value === selectedValue) ?? null;

  return (
    <CycleSelectRow>
      <CycleSelectLabel>{t('Usage Cycle')}</CycleSelectLabel>
      <CycleSelectWrapper>
        <Select
          aria-label={t('Usage Cycle')}
          value={selected}
          options={options}
          onChange={(option: Option) => onChange(option.value)}
          isSearchable={false}
          isClearable={false}
          components={{ Menu: CycleSelectMenu }}
          data-qa-id="ai-usage-cycle-select"
        />
      </CycleSelectWrapper>
    </CycleSelectRow>
  );
};
